/**
 * QuoteCard - Unified quote display component for Hospital and Admin portals
 * Shows supplier quote details with PDF download, email, and approve actions
 */
import React, { useState } from "react";
import { 
  DollarSign, 
  Clock, 
  Download, 
  Mail, 
  Check, 
  X, 
  Building2,
  Package,
  FileText,
  Loader2,
  ExternalLink,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Status badge configuration
const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700" },
  pending_review: { label: "Pending Review", color: "bg-amber-100 text-amber-700" },
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700" },
  sent_to_hospital: { label: "Sent to Hospital", color: "bg-purple-100 text-purple-700" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700" },
  approved_by_hospital: { label: "Hospital Approved", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Declined", color: "bg-red-100 text-red-700" },
  rejected_by_hospital: { label: "Hospital Rejected", color: "bg-rose-100 text-rose-700" },
  ordered: { label: "Ordered", color: "bg-cyan-100 text-cyan-700" },
  no_stock: { label: "No Stock", color: "bg-red-50 text-red-600 border border-red-200" },
  shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-700" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700" }
};

/**
 * QuoteCard Component
 * @param {Object} quote - Quote data object
 * @param {Object} request - Parent backorder request object
 * @param {string} viewMode - "hospital" or "admin"
 * @param {Function} onApprove - Callback when quote is approved
 * @param {Function} onDecline - Callback when quote is declined
 * @param {Function} onRefresh - Callback to refresh quotes after action
 * @param {string} token - Auth token for API calls
 */
export default function QuoteCard({ 
  quote, 
  request, 
  viewMode = "hospital", 
  onApprove, 
  onDecline,
  onRefresh,
  token
}) {
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showHover, setShowHover] = useState(false);
  // Hospital-only: per-quote action drawer with optional note
  const [actionMode, setActionMode] = useState(null); // "approve" | "reject" | null
  const [actionNote, setActionNote] = useState("");

  const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.pending;
  const isAdmin = viewMode === "admin";

  // Download PDF quote
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      // Calculate totals
      const unitPrice = quote.customer_price || quote.unit_price || 0;
      const quantity = quote.quantity || 1;
      const subtotal = unitPrice * quantity;
      const total = subtotal;

      // Transform payload to match QuoteRequest model
      const payload = {
        customer_name: request?.hospital_name || "Hospital",
        customer_email: request?.contact_email || "",
        customer_address: request?.address || "",
        items: [{
          name: quote.item_name || "Item",
          description: `${quote.manufacturer || ""} ${quote.mfr_number ? `(Part #: ${quote.mfr_number})` : ""}`.trim(),
          quantity: quantity,
          unit_price: unitPrice,
          total: subtotal
        }],
        subtotal: subtotal,
        discount_total: 0,
        total: total,
        notes: `Supplier: ${quote.supplier_name}${quote.supplier_contact ? ` (${quote.supplier_contact})` : ""}\nLead Time: ${quote.lead_time_days || 3} days\nQuote ID: ${quote.id}\nHospital Ref: ${request?.id || quote.backorder_id}${quote.notes ? `\n\nSupplier Notes: ${quote.notes}` : ""}`,
        valid_days: 30
      };

      const response = await fetch(`${API}/marketplace/quote/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("PDF generation error:", errorData);
        throw new Error(errorData.detail || "Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quote_${quote.id}_${quote.supplier_name?.replace(/\s+/g, "_") || "supplier"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Email PDF quote
  const handleEmailPdf = async () => {
    const email = prompt("Enter email address to send quote:");
    if (!email) return;

    setEmailing(true);
    try {
      // Calculate totals
      const unitPrice = quote.customer_price || quote.unit_price || 0;
      const quantity = quote.quantity || 1;
      const subtotal = unitPrice * quantity;
      const total = subtotal;

      // Transform payload to match QuoteRequest model
      const payload = {
        customer_name: request?.hospital_name || "Hospital",
        customer_email: email,
        customer_address: request?.address || "",
        items: [{
          name: quote.item_name || "Item",
          description: `${quote.manufacturer || ""} ${quote.mfr_number ? `(Part #: ${quote.mfr_number})` : ""}`.trim(),
          quantity: quantity,
          unit_price: unitPrice,
          total: subtotal
        }],
        subtotal: subtotal,
        discount_total: 0,
        total: total,
        notes: `Supplier: ${quote.supplier_name}${quote.supplier_contact ? ` (${quote.supplier_contact})` : ""}\nLead Time: ${quote.lead_time_days || 3} days\nQuote ID: ${quote.id}\nHospital Ref: ${request?.id || quote.backorder_id}${quote.notes ? `\n\nSupplier Notes: ${quote.notes}` : ""}`,
        valid_days: 30
      };

      const response = await fetch(`${API}/marketplace/quote/email?email_to=${encodeURIComponent(email)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to send email");
      }
      toast.success(`Quote PDF sent to ${email}`);
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to send email");
    } finally {
      setEmailing(false);
    }
  };

  // Approve quote (hospital action) — optional note posted to admin notification
  const handleApprove = async (note = "") => {
    if (onApprove) {
      onApprove(quote.id, note);
      return;
    }

    setApproving(true);
    try {
      const response = await fetch(`${API}/backorders/hospital/quotes/${quote.id}/approve`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ note: note || "" }),
      });

      if (!response.ok) throw new Error("Failed to approve quote");
      toast.success("Quote approved — admin notified");
      setActionMode(null);
      setActionNote("");
      onRefresh?.();
    } catch (error) {
      console.error("Approve error:", error);
      toast.error("Failed to approve quote");
    } finally {
      setApproving(false);
    }
  };

  // Reject quote (hospital action) — note required
  const handleReject = async (note = "") => {
    if (!note.trim()) {
      toast.error("Please add a short note explaining why you're rejecting");
      return;
    }
    if (onDecline) {
      onDecline(quote.id, note);
      return;
    }
    setRejecting(true);
    try {
      const response = await fetch(`${API}/backorders/hospital/quotes/${quote.id}/reject`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ note }),
      });
      if (!response.ok) throw new Error("Failed to reject quote");
      toast.success("Quote rejected — admin notified");
      setActionMode(null);
      setActionNote("");
      onRefresh?.();
    } catch (error) {
      console.error("Reject error:", error);
      toast.error("Failed to reject quote");
    } finally {
      setRejecting(false);
    }
  };

  // Calculate total price
  const totalPrice = quote.total_price || (quote.customer_price || quote.unit_price || 0) * (quote.quantity || 1);

  return (
    <TooltipProvider>
      <div 
        className="bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 hover:shadow-md transition-all relative"
        onMouseEnter={() => setShowHover(true)}
        onMouseLeave={() => setShowHover(false)}
      >
        {/* Quick View Hover Preview */}
        {showHover && (
          <div className="absolute -top-2 -right-2 z-10">
            <Tooltip open={showHover}>
              <TooltipTrigger asChild>
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer">
                  <Eye className="w-2.5 h-2.5 text-white" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs p-3 bg-slate-900 text-white">
                <div className="space-y-2">
                  <p className="font-medium">{quote.supplier_name}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400">Price:</span>
                      <span className="ml-1 font-medium text-green-400">${(quote.customer_price || quote.unit_price)?.toFixed(2)}/unit</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Lead Time:</span>
                      <span className="ml-1">{quote.lead_time_days || "3"} days</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Qty:</span>
                      <span className="ml-1">{quote.quantity}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Total:</span>
                      <span className="ml-1 font-medium text-green-400">${totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                  {quote.notes && (
                    <p className="text-xs text-slate-300 border-t border-slate-700 pt-2 mt-2">
                      {quote.notes.substring(0, 100)}{quote.notes.length > 100 ? "..." : ""}
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Header: Supplier + Status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
              <Building2 className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{quote.supplier_name}</p>
              {quote.supplier_contact && (
                <p className="text-xs text-slate-500">{quote.supplier_contact}</p>
              )}
            </div>
          </div>
          <Badge className={statusConfig.color}>
            {statusConfig.label}
          </Badge>
        </div>

        {/* Item Info */}
        <div className="bg-slate-50 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
            <Package className="w-4 h-4" />
            <span className="font-medium">{quote.item_name || "Item"}</span>
          </div>
          <div className="text-xs text-slate-500 space-y-0.5">
            {quote.manufacturer && <p>Manufacturer: {quote.manufacturer}</p>}
            {quote.mfr_number && <p>Part #: {quote.mfr_number}</p>}
            <p>Quantity: {quote.quantity}</p>
          </div>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-3 gap-2 text-center bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 mb-3 border border-green-100">
          <div>
            <p className="text-xs text-slate-500">Unit Price</p>
            <p className="text-sm font-bold text-green-700">
              ${(quote.customer_price || quote.unit_price)?.toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Lead Time</p>
            <p className="text-sm font-medium text-slate-700 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              {quote.lead_time_days || "3"} days
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-sm font-bold text-green-700">
              ${totalPrice.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Admin-only: Cost/Margin info */}
        {isAdmin && quote.supplier_cost && (
          <div className="grid grid-cols-2 gap-2 text-center bg-amber-50 rounded-lg p-2 mb-3 border border-amber-100">
            <div>
              <p className="text-xs text-amber-600">Supplier Cost</p>
              <p className="text-sm font-medium text-amber-700">${quote.supplier_cost?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-amber-600">Margin</p>
              <p className="text-sm font-medium text-amber-700">
                {quote.customer_price && quote.supplier_cost 
                  ? `${(((quote.customer_price - quote.supplier_cost) / quote.supplier_cost) * 100).toFixed(1)}%`
                  : "—"}
              </p>
            </div>
          </div>
        )}

        {/* Notes */}
        {quote.notes && (
          <div className="text-xs text-slate-600 bg-blue-50 rounded p-2 mb-3 border border-blue-100">
            <p className="font-medium text-blue-700 mb-1">Supplier Notes:</p>
            <p>{quote.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={downloading}
            className="text-slate-600 hover:text-slate-900"
          >
            {downloading ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Download className="w-3 h-3 mr-1" />
            )}
            PDF
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleEmailPdf}
            disabled={emailing}
            className="text-slate-600 hover:text-slate-900"
          >
            {emailing ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Mail className="w-3 h-3 mr-1" />
            )}
            Email
          </Button>

          {/* Approve/Decline buttons - show when not already approved/rejected/no_stock */}
          {quote.status !== "approved" && 
           quote.status !== "approved_by_hospital" && 
           quote.status !== "rejected" &&
           quote.status !== "rejected_by_hospital" &&
           quote.status !== "no_stock" && (
            <>
              {!actionMode ? (
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    size="sm"
                    onClick={() => { setActionMode("approve"); setActionNote(""); }}
                    disabled={approving || rejecting}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`quote-${quote.id}-approve-open`}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => { setActionMode("reject"); setActionNote(""); }}
                    disabled={approving || rejecting}
                    className="text-white"
                    data-testid={`quote-${quote.id}-reject-open`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Reject
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Hospital action drawer with optional/required note */}
        {actionMode && (
          <div
            className={`mt-3 rounded-lg border p-3 ${
              actionMode === "approve"
                ? "bg-emerald-50 border-emerald-200"
                : "bg-rose-50 border-rose-200"
            }`}
            data-testid={`quote-${quote.id}-action-drawer`}
          >
            <p className={`text-xs font-semibold mb-2 ${actionMode === "approve" ? "text-emerald-800" : "text-rose-800"}`}>
              {actionMode === "approve"
                ? "Add an optional note for the admin team (e.g. PO number, ship-to confirmation)."
                : "Tell the admin why this quote isn't a fit (required — they may re-broadcast or call you back)."}
            </p>
            <textarea
              rows={2}
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              placeholder={
                actionMode === "approve"
                  ? "Optional note for admin..."
                  : "Reason (required) — e.g. 'price too high vs spec', 'wrong size', 'lead time too long'..."
              }
              className="w-full text-sm rounded border border-slate-300 px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-300"
              data-testid={`quote-${quote.id}-action-note`}
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setActionMode(null); setActionNote(""); }}
                disabled={approving || rejecting}
              >
                Cancel
              </Button>
              {actionMode === "approve" ? (
                <Button
                  size="sm"
                  onClick={() => handleApprove(actionNote.trim())}
                  disabled={approving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`quote-${quote.id}-approve-confirm`}
                >
                  {approving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                  Confirm Approval
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleReject(actionNote.trim())}
                  disabled={rejecting || !actionNote.trim()}
                  className="text-white"
                  data-testid={`quote-${quote.id}-reject-confirm`}
                >
                  {rejecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
                  Confirm Rejection
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Surface previously-submitted hospital note when status is locked in */}
        {(quote.status === "approved_by_hospital" || quote.status === "rejected_by_hospital") && quote.hospital_action_note && (
          <div
            className={`mt-3 rounded-lg border p-2 text-xs ${
              quote.status === "approved_by_hospital"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
            data-testid={`quote-${quote.id}-action-note-display`}
          >
            <span className="font-semibold">Hospital note:</span> {quote.hospital_action_note}
          </div>
        )}

        {/* No Stock indicator */}
        {quote.status === "no_stock" && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-medium flex items-center gap-1">
              <X className="w-4 h-4" />
              Supplier indicated: No Stock Available
            </p>
            <p className="text-xs text-red-500 mt-1">
              {quote.response || "Item not available from this supplier"}
            </p>
          </div>
        )}

        {/* Submission date */}
        <p className="text-xs text-slate-400 mt-2 text-right">
          {quote.status === "no_stock" ? "Responded" : "Submitted"}: {(quote.submitted_at || quote.created_at) ? new Date(quote.submitted_at || quote.created_at).toLocaleString() : "—"}
        </p>
      </div>
    </TooltipProvider>
  );
}

/**
 * QuoteCardList - Renders a list of QuoteCards
 */
export function QuoteCardList({ 
  quotes, 
  request, 
  viewMode = "hospital", 
  onApprove, 
  onDecline,
  onRefresh,
  token,
  loading = false,
  emptyMessage = "No quotes received yet"
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!quotes || quotes.length === 0) {
    return (
      <div className="text-center py-8 bg-slate-50 rounded-lg border border-slate-200">
        <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500">{emptyMessage}</p>
        <p className="text-xs text-slate-400 mt-1">Quotes will appear here when suppliers respond</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {quotes.map((quote) => (
        <QuoteCard
          key={quote.id}
          quote={quote}
          request={request}
          viewMode={viewMode}
          onApprove={onApprove}
          onDecline={onDecline}
          onRefresh={onRefresh}
          token={token}
        />
      ))}
    </div>
  );
}
